import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { FieldArrayRenderProps } from 'formik/dist/FieldArray'
import { TreeItem, TreeItemLayout } from '@fluentui/react-components/unstable'
import { getFilePartyFromMeta, InvoiceProps, PriceVat } from './InvoiceForm'
import { useFormikContext } from 'formik'
import { FilePartyMetaDataDto, InvoiceMutationDto } from '../../../../config/finance/invoiceConfig'
import { Button, Field, Input, InputOnChangeData, Label, Link, Tooltip } from '@fluentui/react-components'
import { DeleteRegular } from '@fluentui/react-icons'
import { Stack, StackItem } from '@fluentui/react'
import LegacyFilePartyDropdown, { IFilePartyDropdownOption } from '../../../Dropdown/LegacyFilePartyDropdown'
import { getDropdownProps } from '../../../../helpers/formHelper'
import FormattableNumberInput, { CommitEventType } from '../../../Input/FormattableNumberInput'
import useCurrency from '../../../../hooks/useCurrency'
import MoneyText from '../../../Text/Currency/MoneyText'
import { reducePrices } from '../../../../helpers/priceHelper'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatNumber, parseNumber } from '../../../../helpers/numberHelper'

export type ChildProps = FieldArrayRenderProps &
    InvoiceProps & {
        child: InvoiceMutationDto
        index: number
        prefix: string
        isDragging: boolean
        filePartyOptions?: IFilePartyDropdownOption<FilePartyMetaDataDto>[]
    }

function Actions(props: ChildProps): ReactElement {
    return (
        <Stack horizontal verticalAlign="center">
            <StackItem>
                <Tooltip content="Verwijder cliënt" relationship="description" withArrow>
                    <Button
                        appearance="transparent"
                        icon={<DeleteRegular />}
                        onClick={() => props.remove(props.index)}
                    />
                </Tooltip>
            </StackItem>
        </Stack>
    )
}

function Aside(props: ChildProps): ReactElement {
    const { values } = useFormikContext<InvoiceMutationDto>()
    const { currency } = useCurrency(values.currency)

    const totalFactor = useMemo<number>(
        () => values.children?.map(child => child.factor).reduce((a, b) => a + b, 0),
        [values.children],
    )
    const price = useMemo<PriceVat>(
        () =>
            [...props.lineItemGroupPrices.values()].reduce(reducePrices, {
                subtotal: 0,
                vat: [],
                total: 0,
            }),
        [props.lineItemGroupPrices],
    )
    const factoredPrice = useMemo<PriceVat>(() => {
        return {
            subtotal: (price.subtotal / totalFactor) * props.child.factor,
            vat: [],
            total: (price.total / totalFactor) * props.child.factor,
        }
    }, [totalFactor, price])

    return (
        <StackItem>
            <MoneyText amount={factoredPrice.subtotal} currency={currency} textProps={{ size: 400 }} />
        </StackItem>
    )
}

export function InvoiceChildContent(props: ChildProps): ReactElement {
    const history = useHistory()
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<InvoiceMutationDto>()

    useEffect(() => {
        if (props.child.debtorPivotId) {
            const recipient = getFilePartyFromMeta(props.child.debtorPivotId, props.meta.data.fileParties)
            setFieldValue(`${props.prefix}.recipientReference`, props.child.recipientReference ?? recipient?.reference)
            setFieldValue(`${props.prefix}.recipientEmail`, props.child.recipientEmail ?? recipient?.email)
        }
    }, [values.children?.[props.index]?.debtorPivotId])

    return (
        <TreeItem itemType={'leaf'} onClick={event => event.preventDefault()}>
            <TreeItemLayout actions={<Actions {...props} />} aside={<Aside {...props} />}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 'm' }} grow>
                    <Field
                        label={<Label weight={'semibold'}>{translate('base.table.header.number')}</Label>}
                        orientation={'vertical'}
                    >
                        {props.child.id && (
                            <Link onClick={() => history.push(`/finance/invoices/${props.child.id}/edit`)}>
                                {props.child.number}
                            </Link>
                        )}
                        {!props.child.id && <Label>{props.child.number}</Label>}
                    </Field>
                    <StackItem>
                        <LegacyFilePartyDropdown
                            label={translate('invoice.clientpivotid')}
                            required
                            disabled={props.meta.loading}
                            options={props.filePartyOptions}
                            {...getDropdownProps(values.children?.[props.index]?.clientPivotId, undefined, value =>
                                setFieldValue(`${props.prefix}.clientPivotId`, value),
                            )}
                        />
                    </StackItem>
                    <StackItem>
                        <LegacyFilePartyDropdown
                            label={translate('invoice.debtorpivotid')}
                            required
                            disabled={props.meta.loading}
                            options={props.filePartyOptions}
                            {...getDropdownProps(values.children?.[props.index]?.debtorPivotId, undefined, value =>
                                setFieldValue(`${props.prefix}.debtorPivotId`, value),
                            )}
                        />
                    </StackItem>
                    <Field
                        required
                        label={
                            <Label weight={'semibold'}>
                                {translate('invoice.edit.form.header.recipientreference')}
                            </Label>
                        }
                        orientation={'vertical'}
                    >
                        <Input
                            required
                            value={values.children?.[props.index]?.recipientReference}
                            onChange={(_, { value }: InputOnChangeData) =>
                                setFieldValue(`${props.prefix}.recipientReference`, value)
                            }
                        />
                    </Field>
                    <Field
                        required
                        label={<Label weight={'semibold'}>{translate('invoice.edit.form.header.debtorfactor')}</Label>}
                        orientation={'vertical'}
                    >
                        <FormattableNumberInput
                            required
                            allowNegative={false}
                            decimals={2}
                            value={values.children?.[props.index]?.factor}
                            onCommit={(eventType: CommitEventType, value: string) => {
                                if (eventType !== CommitEventType.OnLoad) {
                                    setFieldValue(`${props.prefix}.factor`, parseNumber(value, 'en'))
                                }
                            }}
                        />
                    </Field>
                </Stack>
            </TreeItemLayout>
        </TreeItem>
    )
}
